import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Divider,
  Alert,
} from "@mui/material";

import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useSelector } from "react-redux";
import { apspdclServiceId, entity_id } from "../Utils/Const";
import { baseapi } from "../Environmentalurl";

const TAWalletLogin = () => {
  // ==================================================
  // LOGIN STATE
  // ==================================================

  const [loginStage, setLoginStage] = useState("otp");

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);

  const [tpin, setTpin] = useState(["", "", "", ""]);

  const [loading, setLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [successMessage, setSuccessMessage] = useState("");

  const otpRefs = useRef([]);

  const tpinRefs = useRef([]);

  // --------------------------------------------------
  // Navigation / payment context
  // --------------------------------------------------
  // The electricity page passes the bill data through
  // React Router state. No bill/API response is stored
  // in sessionStorage.
  const location = useLocation();
  const navigate = useNavigate();

  const routeState = location.state || {};

  // The electricity page may redirect to this page using
  // window.location.href, so React Router state is not guaranteed
  // to be available. In that case recover the exact bill details
  // from the response saved by TAWalletElectricity.
  const billInfo = (() => {
    // First preference: React Router state
    if (routeState?.billInfo) {
      return routeState.billInfo;
    }

    try {
      const storedBillResponse = sessionStorage.getItem(
        "taWalletBillFetchResponse",
      );

      if (!storedBillResponse) {
        return null;
      }

      const parsedBillResponse = JSON.parse(storedBillResponse);

      /*
       * APSPDCL response structure:
       *
       * {
       *   Response_Code: "3150",
       *   Message: "Success",
       *   BillerInfo: {
       *      STATUS: "VALID",
       *      SCNO: "...",
       *      CONSUMERNAME: "...",
       *      TOTAMOUNT: "...",
       *      ...
       *   }
       * }
       *
       * BillerInfo itself is the bill object.
       */

      const bill = parsedBillResponse?.BillerInfo;

      if (bill && typeof bill === "object" && !Array.isArray(bill)) {
        return bill;
      }

      // Fallback for older response structure
      if (
        parsedBillResponse?.BillerInfo?.Info &&
        typeof parsedBillResponse.BillerInfo.Info === "object"
      ) {
        return parsedBillResponse.BillerInfo.Info;
      }

      // Another fallback
      if (
        parsedBillResponse?.Info &&
        typeof parsedBillResponse.Info === "object"
      ) {
        return parsedBillResponse.Info;
      }

      return null;
    } catch (error) {
      console.warn(
        "Unable to restore bill details from sessionStorage:",
        error,
      );

      return null;
    }
  })();

  // IMPORTANT:
  // InputMessageRefID must always come from the OTP session stored in
  // sessionStorage. Router state can be lost when the page is opened
  // using window.location.href, so do not depend on routeState alone.
  const [messageRefID, setMessageRefID] = useState(() => {
    const storedMessageRefID =
      sessionStorage.getItem("taWalletMessageRefID") ||
      sessionStorage.getItem("MessageRefID") ||
      sessionStorage.getItem("messageRefID") ||
      "";

    return storedMessageRefID
      ? String(storedMessageRefID)
      : routeState?.messageRefID
        ? String(routeState.messageRefID)
        : "";
  });

  // WalletToken is returned by loginvalidateotpV2 and is
  // kept only in component state for the current login flow.
  const [walletToken, setWalletToken] = useState("");

  // ==================================================
  // TRANSACTION ORDER ID
  // ==================================================
  // One OrderID is used for the COMPLETE transaction:
  //
  //   current transaction
  //       -> login
  //       -> TPIN
  //       -> apspdclbillpayment
  //       -> /payment
  //       -> tapay_cc_submit
  //
  // IMPORTANT:
  // Never reuse an old OrderID from a previous transaction.
  // If the electricity page already supplied a fresh OrderID in
  // Router state, use that exact value. Otherwise generate a NEW
  // OrderID when this login transaction starts.
  const [transactionOrderId] = useState(() => {
    const routeOrderId = routeState?.orderId || routeState?.OrderID || "";

    if (routeOrderId) {
      const freshRouteOrderId = String(routeOrderId);

      sessionStorage.setItem("taWalletTransactionOrderId", freshRouteOrderId);

      return freshRouteOrderId;
    }

    // No active OrderID was supplied for this new login flow.
    // Do NOT read the previous session OrderID.
    const newOrderId = `${Date.now()}${String(
      Math.floor(Math.random() * 1000),
    ).padStart(3, "0")}`;

    // Remove artifacts that belong to the previous transaction.
    sessionStorage.removeItem("taWalletApspdclPaymentRequest");
    sessionStorage.removeItem("tapayAttempt");

    sessionStorage.setItem("taWalletTransactionOrderId", newOrderId);

    console.log("NEW TA Wallet transaction OrderID:", newOrderId);

    return newOrderId;
  });

  // The OTP API's MessageRefID is the value required as
  // InputMessageRefID for loginvalidateotpV2.
  const taWalletMessageRefID =
    sessionStorage.getItem("taWalletMessageRefID") ||
    sessionStorage.getItem("MessageRefID") ||
    sessionStorage.getItem("messageRefID") ||
    messageRefID ||
    "";

  if (transactionOrderId) {
    sessionStorage.setItem(
      "taWalletTransactionOrderId",
      String(transactionOrderId),
    );
  }

  // ==================================================
  // OTP CHANGE
  // ==================================================

  const handleOtpChange = (index, value) => {
    const numericValue = value.replace(/\D/g, "");

    const updatedOtp = [...otp];

    updatedOtp[index] = numericValue.charAt(numericValue.length - 1);

    setOtp(updatedOtp);

    setErrorMessage("");

    if (numericValue && index < otp.length - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  // ==================================================
  // OTP BACKSPACE
  // ==================================================

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // ==================================================
  // RESEND OTP
  // ==================================================

  const handleResendOtp = async () => {
    if (loading) {
      return;
    }

    const mobileNumber = sessionStorage.getItem("taWalletMobile") || "";

    if (mobileNumber.length !== 10) {
      setErrorMessage(
        "Mobile number is not available. Please start the login again.",
      );
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setLoading(true);

    const requestBody = {
      ReqType: "One_userlogin",
      MobileNumber: mobileNumber,
      EntityID: entity_id,
      SSOID: "-",
      MemberID: "26384597149",
      Statement: "LoginWithOTP",
      MachineID: "b3c8d684-0b76-4858-afd3-d4a6d977724f",
      Platform: 1,
      Geolocation: "17.111,17.99",
    };

    try {
      const response = await fetch(
        `${baseapi}/auth/walletexistance`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      let responseData = null;

      try {
        responseData = await response.json();
      } catch {
        responseData = null;
      }

      const responseCode = String(responseData?.Response_Code ?? "").trim();

      if (!response.ok || responseCode !== "1110") {
        setErrorMessage(
          responseData?.Message ||
            responseData?.message ||
            "Unable to resend OTP. Please try again.",
        );
        return;
      }

      if (responseData?.MessageRefID) {
        const newMessageRefID = String(responseData.MessageRefID);

        setMessageRefID(newMessageRefID);

        // Persist the latest OTP MessageRefID because this exact
        // value must be sent as InputMessageRefID during OTP
        // verification.
        sessionStorage.setItem("taWalletMessageRefID", newMessageRefID);
        sessionStorage.setItem("MessageRefID", newMessageRefID);
        sessionStorage.setItem("messageRefID", newMessageRefID);
      }

      setSuccessMessage("OTP resent successfully.");

      window.setTimeout(() => {
        setSuccessMessage((current) =>
          current === "OTP resent successfully." ? "" : current,
        );
      }, 3000);
    } catch (error) {
      console.error("TA Wallet Resend OTP Error:", error);

      setErrorMessage("Unable to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ==================================================
  // VERIFY OTP
  // ==================================================

  const handleVerifyOtp = async () => {
    // Clear any previous error immediately when Verify OTP is clicked.
    setErrorMessage("");
    setSuccessMessage("");

    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      setErrorMessage("Please enter the 6-digit OTP.");

      return;
    }

    // Always read the OTP MessageRefID from sessionStorage first.
    // This prevents Router navigation/re-rendering from losing the
    // MessageRefID required by loginvalidateotpV2.
    const currentMessageRefID =
      sessionStorage.getItem("taWalletMessageRefID") ||
      sessionStorage.getItem("MessageRefID") ||
      sessionStorage.getItem("messageRefID") ||
      messageRefID ||
      taWalletMessageRefID ||
      "";

    if (!currentMessageRefID) {
      setErrorMessage("OTP session has expired. Please resend the OTP.");
      return;
    }

    // Keep the component/session values synchronized.
    setMessageRefID(String(currentMessageRefID));
    sessionStorage.setItem("taWalletMessageRefID", String(currentMessageRefID));

    setLoading(true);

    const requestBody = {
      EntityID: entity_id,
      ApplicationType: 1,
      Platform: 1,
      ReqType: "One_loginvalidotpV2",
      InputMessageRefID: String(currentMessageRefID),
      OTP: enteredOtp,
      DeviceID:
        "d8StqY8kSK65YINoATrGvH:APA91bG7sxFgCsnOqubyCAzm_H5mnI64uDXFOJ0AH78IMWSEJkZC7PmTTZkh4zG9nBnI0icROb35jKDgD7tRzBN38PVWZhzpmeav_K6K31dy_jdlprtbaktd",
      Statement: "LoginValidOTP",
      MachineID: "b3c8d684-0b76-4858-afd3-d4a6d977724f",
    };

    try {
      const response = await fetch(
         `${baseapi}/auth/loginvalidateotpV2`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      let responseData = null;

      try {
        responseData = await response.json();
      } catch {
        responseData = null;
      }

      const responseCode = String(responseData?.Response_Code ?? "").trim();

      if (!response.ok || responseCode !== "1000") {
        setErrorMessage(
          responseData?.Message ||
            responseData?.message ||
            "Invalid OTP. Please try again.",
        );
        return;
      }

      if (!responseData?.WalletToken) {
        setErrorMessage("Wallet token was not returned. Please try again.");
        return;
      }

      // Keep the token only in React state. It is never
      // written to sessionStorage/localStorage.
      setWalletToken(String(responseData.WalletToken));

      setErrorMessage("");
      setSuccessMessage("OTP verified successfully.");

      // Keep the success message on the OTP screen
      // briefly before moving to TPIN.
      window.setTimeout(() => {
        setErrorMessage("");
        setSuccessMessage("");
        setLoginStage("tpin");
      }, 1200);
    } catch (error) {
      console.error("TA Wallet OTP Validation Error:", error);

      setErrorMessage("Unable to validate OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ==================================================
  // TPIN CHANGE
  // ==================================================

  const handleTpinChange = (index, value) => {
    const numericValue = value.replace(/\D/g, "");

    const updatedTpin = [...tpin];

    updatedTpin[index] = numericValue.charAt(numericValue.length - 1);

    setTpin(updatedTpin);

    setErrorMessage("");

    if (numericValue && index < tpin.length - 1) {
      tpinRefs.current[index + 1]?.focus();
    }
  };

  // ==================================================
  // TPIN BACKSPACE
  // ==================================================

  const handleTpinKeyDown = (index, event) => {
    if (event.key === "Backspace" && !tpin[index] && index > 0) {
      tpinRefs.current[index - 1]?.focus();
    }
  };

  // ==================================================
  // LOGIN
  // ==================================================

  const handleLogin = async () => {
    const enteredTpin = tpin.join("");

    if (enteredTpin.length !== 4) {
      setErrorMessage("Please enter your 4-digit TPIN.");

      return;
    }

    // Only the mobile number is intentionally kept in
    // sessionStorage, as required by the flow.
    const mobileNumber = sessionStorage.getItem("taWalletMobile") || "";

    // WalletToken is held in React state from OTP validation.
    const currentWalletToken = walletToken;

    if (mobileNumber.length !== 10) {
      setErrorMessage(
        "Mobile number is not available. Please start the login again.",
      );

      return;
    }

    if (!currentWalletToken) {
      setErrorMessage(
        "Wallet session has expired. Please verify the OTP again.",
      );

      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setLoading(true);

    const requestBody = {
      ReqType: "LoginWithTPIN",
      Mobilenumber: mobileNumber,
      Statement: "ValidateTPIN",
      TPIN: enteredTpin,
      EntityID: entity_id,
      MachineID: "b3c8d684-0b76-4858-afd3-d4a6d977724f",
      Platform: 1,
      GeoLocation: "17.4357,78.4574",
      IsTPINEnabled: "True",
      WalletToken: currentWalletToken,
    };

    try {
      // ==================================================
      // 1. VALIDATE TPIN
      // ==================================================

      const response = await fetch(
         `${baseapi}/auth/loginwithtpin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        },
      );

      let responseData = null;

      try {
        responseData = await response.json();
      } catch {
        responseData = null;
      }

      const responseCode = String(responseData?.Response_Code ?? "").trim();

      if (!response.ok || responseCode !== "1000") {
        setErrorMessage(
          responseData?.Message ||
            responseData?.message ||
            "Invalid TPIN. Please try again.",
        );

        return;
      }

      // ==================================================
      // 2. STORE SESSION ID FROM TPIN RESPONSE
      // ==================================================

      // loginwithtpin returns SessionID inside CustomerProfile.
      // This is the SessionID that must be used by the payment
      // page for tagetpayucardinfo. Do not reuse the OTP SessionID.

      /*
       * Depending on the wallet API response, Balance can
       * be returned directly or inside CustomerProfile.
       * CustomerProfile is sometimes returned as a JSON
       * string, so handle both formats.
       */

      let customerProfile = null;

      try {
        const customerProfileValue =
          responseData?.Info?.find?.((item) => item?.CustomerProfile)
            ?.CustomerProfile ?? responseData?.CustomerProfile;

        if (typeof customerProfileValue === "string") {
          const parsedProfile = JSON.parse(customerProfileValue);

          customerProfile = Array.isArray(parsedProfile)
            ? parsedProfile[0]
            : parsedProfile;
        } else if (Array.isArray(customerProfileValue)) {
          customerProfile = customerProfileValue[0];
        } else if (
          customerProfileValue &&
          typeof customerProfileValue === "object"
        ) {
          customerProfile = customerProfileValue;
        }
      } catch (profileParseError) {
        console.warn("Unable to parse CustomerProfile:", profileParseError);
      }

      const sessionIDValue =
        responseData?.SessionID ??
        responseData?.sessionID ??
        customerProfile?.SessionID ??
        customerProfile?.sessionID ??
        "";

      if (!sessionIDValue) {
        setErrorMessage(
          "Wallet Session ID was not returned by login. Please try again.",
        );
        return;
      }

      const sessionIDString = String(sessionIDValue);

      // Store the SessionID returned specifically by loginwithtpin.
      sessionStorage.setItem("taWalletSessionID", sessionIDString);

      // Keep the generic key too because the existing payment flow
      // may read SessionID directly. Both keys contain the same value.
      sessionStorage.setItem("SessionID", sessionIDString);

      console.log("TPIN SessionID stored:", sessionIDString);

      const walletBalanceValue =
        responseData?.Balance ??
        responseData?.balance ??
        customerProfile?.Balance ??
        customerProfile?.balance ??
        null;

      const walletBalance = Number(walletBalanceValue);

      if (
        walletBalanceValue === null ||
        walletBalanceValue === undefined ||
        walletBalanceValue === "" ||
        !Number.isFinite(walletBalance) ||
        walletBalance < 0
      ) {
        setErrorMessage("Unable to read the wallet balance. Please try again.");

        return;
      }

      // ==================================================
      // 3. READ BILL DETAILS
      // ==================================================
      // The bill data was passed to this page through
      // React Router state from TAWalletElectricity.
      // No complete API response is stored in sessionStorage.

      const rawBillAmount =
        billInfo?.TOTAMOUNT ??
        billInfo?.TOTALAMOUNT ??
        billInfo?.totalAmount ??
        billInfo?.totalamount;

      const billAmount = Number(rawBillAmount);

      if (!billInfo) {
        setErrorMessage(
          "Bill details are not available. Please fetch the bill again.",
        );
        return;
      }

      if (
        rawBillAmount === undefined ||
        rawBillAmount === null ||
        rawBillAmount === "" ||
        !Number.isFinite(billAmount) ||
        billAmount < 0
      ) {
        setErrorMessage("Unable to read the electricity bill amount.");
        return;
      }

      // ==================================================
      // 4. RESOLVE THE SINGLE TRANSACTION ORDER ID
      // ==================================================
      //
      // This exact value is used by:
      //   1. apspdclbillpayment
      //   2. tapay_cc_submit on /payment
      //
      // Resolve it BEFORE the balance decision because both
      // branches can navigate to /payment.
      // Use the OrderID created for THIS transaction.
      // Do not fall back to an older session OrderID.
      const finalTransactionOrderId = String(transactionOrderId || "");

      if (!finalTransactionOrderId) {
        setErrorMessage(
          "Transaction Order ID is missing. Please start the transaction again.",
        );
        return;
      }

      sessionStorage.setItem(
        "taWalletTransactionOrderId",
        finalTransactionOrderId,
      );

      // ==================================================
      // 5. COMPARE WALLET BALANCE WITH BILL AMOUNT
      // ==================================================

      /*
       * Required flow:
       *
       * balance > bill amount
       *       -> APSPDCL bill payment API
       *
       * balance <= bill amount
       *       -> /payment
       */

      if (walletBalance <= billAmount) {
        navigate("/payment", {
          state: {
            reason: "INSUFFICIENT_BALANCE",

            // IMPORTANT:
            // Pass the exact transaction OrderID to /payment.
            // Payment page will persist and reuse this same value.
            orderId: finalTransactionOrderId,
            OrderID: finalTransactionOrderId,

            walletBalance,
            billAmount,
            billInfo,
            mobileNumber,
            serviceNumber:
              billInfo?.SCNO ||
              sessionStorage.getItem("taWalletServiceNumber") ||
              "",
          },
        });

        return;
      }

      // ==================================================
      // 6. WALLET BALANCE IS GREATER
      //    CALL APSPDCL BILL PAYMENT API
      // ==================================================

      /*
       * Reuse the SAME OrderID created for this transaction.
       * It is stored in sessionStorage only so the payment page
       * can continue the exact same transaction.
       */
      /*
       * SessionID can be returned directly by the TPIN
       * response or inside CustomerProfile.
       */
      // Reuse the exact SessionID stored after loginwithtpin.
      const sessionIDForPayment = sessionStorage.getItem("taWalletSessionID");

      if (!sessionIDForPayment) {
        setErrorMessage(
          "Wallet Session ID is not available. Please login again.",
        );
        return;
      }

      const apspdclPaymentRequest = {
        ReqType: "APSPDCL_Paybill",

        Platform: "1",

        EntityID: entity_id,

        MachineID: "0409634b9bc2de2c",

        Mobilenumber: mobileNumber,

        SessionID: String(sessionIDForPayment),

        // EXACT same OrderID that tapay_cc_submit will use.
        OrderID: finalTransactionOrderId,

        SCNO:
          billInfo?.SCNO ||
          sessionStorage.getItem("taWalletServiceNumber") ||
          "",

        Circle: billInfo?.CIRCLENAME || "",

        ERO: billInfo?.EROCODE || "",

        PAYMODE: "DEBIT",

        PAYCHANNEL: "PGW",

        CHECKOUTAMOUNT: String(billAmount),

        SCTYPE: billInfo?.SCTYPE || "",

        CONSUMERNAME: billInfo?.CONSUMERNAME || "",

        ARREARS: String(billInfo?.ARREARS ?? "0"),

        CURRENTDEMAND: String(billInfo?.CURRENTDEMAND ?? "0"),

        ACDAMT: String(billInfo?.ACDAMT ?? "0"),

        RCFEES: String(billInfo?.RCFEES ?? "0"),

        Amount: String(billAmount),

        RDGDATE: billInfo?.RDGDATE || "",

        CHECKSUM: billInfo?.CHECKSUM || "",

        TPIN: enteredTpin,

        AdvancePayment: "0",

        DUEDATE: billInfo?.DUEDATE || "",

        DISCDATE: billInfo?.DISCDATE || "",

        GeoLocation: "12.9163182,77.6106841",

        Date: (() => {
          const today = new Date();

          const year = today.getFullYear();

          const month = String(today.getMonth() + 1).padStart(2, "0");

          const day = String(today.getDate()).padStart(2, "0");

          return `${year}-${month}-${day}`;
        })(),

        ServiceID: apspdclServiceId,
      };

      setSuccessMessage(
        "Wallet balance is sufficient. Processing electricity payment...",
      );

      // ==================================================
      // 7. CALL APSPDCL PAYMENT API
      // ==================================================

      // Save the exact request that is about to be sent.
      // Its OrderID is the same OrderID that will be passed to /payment.
      sessionStorage.setItem(
        "taWalletApspdclPaymentRequest",
        JSON.stringify(apspdclPaymentRequest),
      );

      const paymentResponse = await fetch(
        `${baseapi}/auth/apspdclbillpayment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(apspdclPaymentRequest),
        },
      );

      let paymentResponseData = null;

      try {
        paymentResponseData = await paymentResponse.json();
      } catch {
        paymentResponseData = null;
      }

      const paymentResponseCode = String(
        paymentResponseData?.Response_Code ??
          paymentResponseData?.responseCode ??
          "",
      ).trim();

      if (!paymentResponse.ok || paymentResponseCode !== "1000") {
        setErrorMessage(
          paymentResponseData?.Message ||
            paymentResponseData?.message ||
            "Electricity bill payment failed. Please try again.",
        );

        setSuccessMessage("");

        return;
      }

      // Payment API succeeded. Pass the result to the
      // payment page through React Router state.
      setErrorMessage("");
      setSuccessMessage("Electricity bill payment successful.");

      window.setTimeout(() => {
        navigate("/payment", {
          state: {
            reason: "APSPDCL_PAYMENT_SUCCESS",

            // IMPORTANT:
            // This is the SAME OrderID used in apspdclbillpayment.
            orderId: finalTransactionOrderId,
            OrderID: finalTransactionOrderId,

            walletBalance,
            billAmount,
            billInfo,
            mobileNumber,
            serviceNumber:
              billInfo?.SCNO ||
              sessionStorage.getItem("taWalletServiceNumber") ||
              "",
            apspdclPaymentRequest,
            apspdclPaymentResponse: paymentResponseData,
          },
        });
      }, 800);
    } catch (error) {
      console.error("TA Wallet TPIN / APSPDCL Payment Error:", error);

      setSuccessMessage("");

      setErrorMessage(
        "Unable to process the electricity payment. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ==================================================
  // HEADER
  // ==================================================

  const renderHeader = () => {
    return (
      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr 1.5fr 1fr",
            sm: "1fr 2fr 1fr",
          },

          alignItems: "center",

          width: "100%",

          mb: {
            xs: 4,
            sm: 5,
          },

          gap: {
            xs: 1,
            sm: 2,
          },
        }}
      >
        {/* APSPDCL */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src="/assets/apspdcl.jpg"
            alt="APSPDCL Logo"
            sx={{
              height: {
                xs: 42,
                sm: 52,
                md: 58,
              },

              maxWidth: {
                xs: 110,
                sm: 145,
                md: 175,
              },

              width: "auto",

              objectFit: "contain",
            }}
          />
        </Box>

        {/* TITLE */}

        <Box
          sx={{
            textAlign: "center",

            display: "flex",

            flexDirection: "column",

            alignItems: "center",

            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: 21,
                sm: 25,
                md: 28,
              },

              fontWeight: 700,

              color: "#17221d",

              lineHeight: 1.2,

              whiteSpace: "nowrap",
            }}
          >
            TA Wallet Login
          </Typography>

          <Typography
            sx={{
              mt: 0.8,

              fontSize: {
                xs: 10.5,
                sm: 12,
                md: 13,
              },

              color: "#6d7772",

              lineHeight: 1.4,

              textAlign: "center",
            }}
          >
            Login securely to continue with your payment.
          </Typography>
        </Box>

        {/* TA WALLET */}

        <Box
          sx={{
            display: "flex",

            justifyContent: "flex-end",

            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src="/assets/ta_walletlogo.png"
            alt="TA Wallet Logo"
            sx={{
              height: {
                xs: 34,
                sm: 42,
                md: 48,
              },

              maxWidth: {
                xs: 105,
                sm: 140,
                md: 165,
              },

              width: "auto",

              objectFit: "contain",
            }}
          />
        </Box>
      </Box>
    );
  };

  // ==================================================
  // MOBILE SCREEN
  // ==================================================

  // ==================================================
  // OTP SCREEN
  // ==================================================

  const renderOtp = () => {
    return (
      <Box
        sx={{
          maxWidth: 560,
          mx: "auto",
        }}
      >
        <LoginHeading
          icon={<PhoneAndroidIcon />}
          title="Enter OTP"
          description="Enter the 6-digit OTP sent to your registered mobile number."
        />

        <Box
          sx={{
            display: "flex",

            justifyContent: "center",

            gap: {
              xs: 0.8,
              sm: 1.2,
            },

            mt: 3,
          }}
        >
          {otp.map((value, index) => (
            <TextField
              key={index}
              inputRef={(element) => {
                otpRefs.current[index] = element;
              }}
              value={value}
              onChange={(event) => handleOtpChange(index, event.target.value)}
              onKeyDown={(event) => handleOtpKeyDown(index, event)}
              inputProps={{
                maxLength: 1,

                inputMode: "numeric",

                style: {
                  textAlign: "center",
                  fontSize: 18,
                  fontWeight: 600,
                },
              }}
              sx={{
                width: {
                  xs: 42,
                  sm: 50,
                },

                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },

                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#16834d",
                  },
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 1,
          }}
        >
          <Button
            variant="text"
            onClick={handleResendOtp}
            disabled={loading}
            sx={{
              color: "#16834d",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 600,
              minWidth: "auto",
              p: 0.5,
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Resend OTP
          </Button>
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={handleVerifyOtp}
          disabled={loading}
          sx={primaryButtonStyles}
        >
          {loading ? "Verifying..." : "Verify OTP"}
        </Button>
      </Box>
    );
  };

  // ==================================================
  // TPIN SCREEN
  // ==================================================

  const renderTpin = () => {
    return (
      <Box
        sx={{
          maxWidth: 560,
          mx: "auto",
        }}
      >
        <LoginHeading
          icon={<LockOutlinedIcon />}
          title="Enter your TPIN"
          description="Enter your 4-digit TPIN to securely authorize your login."
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: {
              xs: 1,
              sm: 1.5,
            },
            mt: 3,
            width: "100%",
          }}
        >
          {tpin.map((value, index) => (
            <TextField
              key={index}
              inputRef={(element) => {
                tpinRefs.current[index] = element;
              }}
              value={value}
              onChange={(event) => handleTpinChange(index, event.target.value)}
              onKeyDown={(event) => handleTpinKeyDown(index, event)}
              type="password"
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
                style: {
                  textAlign: "center",
                  fontSize: 20,
                  fontWeight: 700,
                },
              }}
              sx={{
                width: {
                  xs: 50,
                  sm: 58,
                },

                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },

                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#16834d",
                  },
              }}
            />
          ))}
        </Box>

        <Alert
          icon={<ShieldOutlinedIcon />}
          severity="info"
          sx={{
            mt: 3,
            textAlign: "left",
            borderRadius: "8px",
            backgroundColor: "#f5faf7",
            color: "#41604f",
            fontSize: 12,
          }}
        >
          Never share your TPIN with anyone. TA Wallet will never ask you to
          disclose your TPIN.
        </Alert>

        <Button
          fullWidth
          variant="contained"
          onClick={handleLogin}
          disabled={loading}
          endIcon={<ArrowForwardIcon />}
          sx={primaryButtonStyles}
        >
          {loading ? "Logging in..." : "Login"}
        </Button>
      </Box>
    );
  };

  // ==================================================
  // MAIN
  // ==================================================

  return (
    <Box
      sx={{
        minHeight: "100vh",

        width: "100%",

        backgroundColor: "#ffffff",

        display: "flex",

        justifyContent: "center",

        alignItems: "center",

        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },

        py: {
          xs: 2,
          sm: 3,
          md: 4,
        },

        boxSizing: "border-box",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",

          maxWidth: 650,

          borderRadius: {
            xs: "12px",
            sm: "16px",
          },

          border: "1px solid #dfe7e3",

          backgroundColor: "#ffffff",

          boxShadow: "0 10px 35px rgba(22,55,39,0.08)",

          overflow: "hidden",

          position: "relative",

          "&::before": {
            content: '""',

            position: "absolute",

            top: 0,

            left: 0,

            right: 0,

            height: "3px",

            backgroundColor: "#16834d",
          },
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 2.5,
              sm: 4,
              md: 5,
            },

            "&:last-child": {
              pb: {
                xs: 2.5,
                sm: 4,
                md: 5,
              },
            },
          }}
        >
          {renderHeader()}

          {/* =========================
                            LOGIN CONTENT
                        ========================= */}

          {loginStage === "otp" && renderOtp()}

          {loginStage === "tpin" && renderTpin()}

          {/* =========================
                            MESSAGES
                        ========================= */}

          {errorMessage && (
            <Alert
              severity="error"
              sx={{
                mt: 3,

                borderRadius: "8px",

                fontSize: 12.5,
              }}
            >
              {errorMessage}
            </Alert>
          )}

          {successMessage && loginStage === "otp" && (
            <Alert
              severity="success"
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
              sx={{
                mt: 3,
                borderRadius: "8px",
                backgroundColor: "#eef8f2",

                color: "#17663d",

                border: "1px solid #cde8d8",

                fontSize: 12.5,
              }}
            >
              {successMessage}
            </Alert>
          )}
        </CardContent>

        <Divider />

        {/* FOOTER */}

        <Box
          sx={{
            py: 1.8,

            px: 2,

            textAlign: "center",

            backgroundColor: "#fcfdfc",
          }}
        >
          <Typography
            sx={{
              fontSize: 11.5,

              color: "#89938e",
            }}
          >
            Your login details are securely processed through TA Wallet.
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

// ======================================================
// LOGIN HEADING
// ======================================================

const LoginHeading = ({ icon, title, description }) => {
  return (
    <Box
      sx={{
        textAlign: "center",

        mb: 3,
      }}
    >
      {/* ICON + TITLE SAME LINE */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 48,

            height: 48,

            borderRadius: "12px",

            backgroundColor: "#edf7f1",

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            color: "#16834d",

            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              fontSize: 25,

              color: "#16834d",
            },
          })}
        </Box>

        <Typography
          sx={{
            fontSize: {
              xs: 19,
              sm: 21,
            },

            fontWeight: 700,

            color: "#1c2822",

            lineHeight: 1.3,

            textAlign: "left",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: 13,

          color: "#7a837f",

          mt: 1.2,

          maxWidth: 520,

          mx: "auto",

          lineHeight: 1.6,

          textAlign: "center",
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

// ======================================================
// TEXT FIELD STYLE
// ======================================================

const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",

    backgroundColor: "#ffffff",

    fontSize: 14,
  },

  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#8cae9d",
  },

  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#16834d",

    borderWidth: "1px",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#16834d",
  },
};

// ======================================================
// PRIMARY BUTTON
// ======================================================

const primaryButtonStyles = {
  mt: 2,

  height: 42,

  borderRadius: "8px",

  backgroundColor: "#16834d",

  textTransform: "none",

  fontWeight: 600,

  boxShadow: "none",

  "&:hover": {
    backgroundColor: "#116b3f",

    boxShadow: "none",
  },

  "&:disabled": {
    backgroundColor: "#dce6e0",

    color: "#89948e",
  },
};

export default TAWalletLogin;
